/**
 * Facets Enhancements - Real-time Product Count Updates
 * Provides real-time feedback for filter selections and product counts
 */
class FacetsEnhancements {
  constructor() {
    this.productCountElements = {
      mobile: document.querySelector('#ProductCount'),
      desktop: document.querySelector('#ProductCountDesktop'),
    };

    this.filterCheckboxes = document.querySelectorAll(
      '.facets-drawer__filter-option input[type="checkbox"]',
    );
    this.sortRadios = document.querySelectorAll(
      '.facets-drawer__option input[type="radio"]',
    );
    this.priceInputs = document.querySelectorAll(
      '.facets-drawer__price-field input',
    );

    this.currentCounts = {
      mobile: this.getProductCount(this.productCountElements.mobile),
      desktop: this.getProductCount(this.productCountElements.desktop),
    };

    this.init();
  }

  init() {
    if (
      !this.productCountElements.mobile &&
      !this.productCountElements.desktop
    ) {
      return;
    }

    this.setupEventListeners();
    this.initializeCollectionCounts();
  }

  setupEventListeners() {
    // Listen for filter changes
    this.filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        this.handleFilterChange(e.target);
      });

      // Add hover effects for non-disabled filters
      const filterOption = checkbox.closest('.facets-drawer__filter-option');
      if (!filterOption.classList.contains('disabled')) {
        filterOption.addEventListener('mouseenter', () => {
          this.addHoverEffect(filterOption);
        });

        filterOption.addEventListener('mouseleave', () => {
          this.removeHoverEffect(filterOption);
        });
      }
    });

    // Listen for sort changes
    this.sortRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        this.handleSortChange(e.target);
      });
    });

    // Listen for price input changes
    this.priceInputs.forEach((input) => {
      input.addEventListener('input', (e) => {
        this.handlePriceChange(e.target);
      });
    });

    // Listen for drawer apply button
    const applyButton = document.querySelector('[data-apply-filters]');
    if (applyButton) {
      applyButton.addEventListener('click', () => {
        this.showLoadingState();
      });
    }

    // Listen for clear all filters
    const clearButton = document.querySelector('[data-clear-filters]');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.showLoadingState();
      });
    }
  }

  handleFilterChange(checkbox) {
    const filterGroup = checkbox.closest('.facets-drawer__filter-group');
    const filterName = filterGroup.querySelector(
      '.facets-drawer__filter-name',
    ).textContent;
    const isChecked = checkbox.checked;
    const filterLabel = checkbox.nextElementSibling.textContent;
    const filterOption = checkbox.closest('.facets-drawer__filter-option');

    // Update visual feedback
    this.updateFilterVisualFeedback(filterOption, isChecked);

    // Update disabled state for related filters
    this.updateRelatedFiltersState(checkbox);

    // Calculate estimated product count
    this.updateEstimatedProductCount();

    // Announce to screen readers
    this.announceFilterChange(filterName, filterLabel, isChecked);

    // Add selection animation
    this.addSelectionAnimation(filterOption, isChecked);
  }

  handleSortChange(radio) {
    const sortLabel = radio.nextElementSibling.textContent;
    this.announceSortChange(sortLabel);
  }

  handlePriceChange(input) {
    // Debounce price input changes
    clearTimeout(this.priceTimeout);
    this.priceTimeout = setTimeout(() => {
      this.updateEstimatedProductCount();
    }, 300);
  }

  updateFilterVisualFeedback(filterOption, isChecked) {
    if (isChecked) {
      filterOption.classList.add('is-checked');
    } else {
      filterOption.classList.remove('is-checked');
    }
  }

  updateRelatedFiltersState(changedCheckbox) {
    const filterGroup = changedCheckbox.closest('.facets-drawer__filter-group');
    const allCheckboxes = filterGroup.querySelectorAll(
      'input[type="checkbox"]',
    );

    allCheckboxes.forEach((checkbox) => {
      const filterOption = checkbox.closest('.facets-drawer__filter-option');
      const count = parseInt(
        checkbox.nextElementSibling.getAttribute('data-count') || '0',
      );

      // Disable filters with 0 results
      if (count === 0 && !checkbox.checked) {
        filterOption.classList.add('disabled');
        checkbox.disabled = true;
      } else {
        filterOption.classList.remove('disabled');
        checkbox.disabled = false;
      }
    });
  }

  addHoverEffect(filterOption) {
    if (!filterOption.classList.contains('disabled')) {
      filterOption.classList.add('is-hovered');
    }
  }

  removeHoverEffect(filterOption) {
    if (!filterOption.classList.contains('disabled')) {
      filterOption.classList.remove('is-hovered');
    }
  }

  addSelectionAnimation(filterOption, isChecked) {
    if (isChecked) {
      filterOption.classList.add('is-animating-in');
      setTimeout(() => {
        filterOption.classList.remove('is-animating-in');
      }, 200);
    } else {
      filterOption.classList.add('is-animating-out');
      setTimeout(() => {
        filterOption.classList.remove('is-animating-out');
      }, 200);
    }
  }

  updateEstimatedProductCount() {
    const activeFilters = this.getActiveFiltersCount();
    const totalProducts = this.getTotalProductsCount();

    let estimatedCount = totalProducts;
    if (activeFilters > 0) {
      estimatedCount = Math.max(
        1,
        Math.floor(totalProducts * Math.pow(0.7, activeFilters)),
      );
    }

    this.updateProductCountDisplay(estimatedCount, true);
  }

  updateProductCountDisplay(count, isEstimated = false) {
    const countText = this.formatCountText(count, isEstimated);

    if (this.productCountElements.mobile) {
      this.animateCountChange(this.productCountElements.mobile, countText);
    }

    if (this.productCountElements.desktop) {
      this.animateCountChange(this.productCountElements.desktop, countText);
    }
  }

  animateCountChange(element, newText) {
    element.classList.add('is-updating');

    setTimeout(() => {
      element.textContent = newText;
      element.classList.remove('is-updating');
    }, 150);
  }

  formatCountText(count, isEstimated) {
    const countNumber = parseInt(count);

    if (isEstimated) {
      return `~${countNumber} products`;
    }

    if (countNumber === 1) {
      return '1 product';
    }

    return `${countNumber} products`;
  }

  getActiveFiltersCount() {
    let count = 0;

    this.filterCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) count++;
    });

    this.priceInputs.forEach((input) => {
      if (input.value && input.value !== input.placeholder) count++;
    });

    return count;
  }

  getTotalProductsCount() {
    const mobileText = this.getProductCount(this.productCountElements.mobile);
    const desktopText = this.getProductCount(this.productCountElements.desktop);

    const mobileMatch = mobileText.match(/(\d+)/);
    const desktopMatch = desktopText.match(/(\d+)/);

    if (mobileMatch) return parseInt(mobileMatch[1]);
    if (desktopMatch) return parseInt(desktopMatch[1]);

    return 0;
  }

  getProductCount(element) {
    if (!element) return '';
    return element.textContent.trim();
  }

  showLoadingState() {
    if (this.productCountElements.mobile) {
      this.productCountElements.mobile
        .closest('.product-count')
        .classList.add('loading');
    }

    if (this.productCountElements.desktop) {
      this.productCountElements.desktop
        .closest('.product-count')
        .classList.add('loading');
    }
  }

  announceFilterChange(filterName, filterLabel, isChecked) {
    const action = isChecked ? 'added' : 'removed';
    const announcement = `${filterLabel} filter ${action} from ${filterName}`;
    this.announceToScreenReader(announcement);
  }

  announceSortChange(sortLabel) {
    const announcement = `Sort changed to ${sortLabel}`;
    this.announceToScreenReader(announcement);
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  initializeCollectionCounts() {
    const collectionButtons = document.querySelectorAll(
      '.collection-nav-button',
    );

    collectionButtons.forEach((button) => {
      const countElement = button.querySelector(
        '.collection-nav-button__count',
      );
      if (countElement) {
        countElement.classList.add('fade-in');
      }
    });
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.facetsEnhancements = new FacetsEnhancements();
});

// Also handle dynamic content loading
document.addEventListener('shopify:section:load', () => {
  if (window.facetsEnhancements) {
    window.facetsEnhancements = new FacetsEnhancements();
  }
});
